import { useMemo, useSyncExternalStore } from 'react';
import axios from 'axios';

const EMPTY_DCF_SAVES_SNAPSHOT = {
  saves: [],
  error: '',
  status: 'idle',
};

const sortDcfSaves = (saves) =>
  saves.toSorted((a, b) => {
    const dateA = Date.parse(a?.updated_at || a?.created_at || '') || 0;
    const dateB = Date.parse(b?.updated_at || b?.created_at || '') || 0;
    return dateB - dateA;
  });

function createDcfSavesStore({
  apiUrl,
  getAccessTokenSilently,
  isAuthenticated,
  ticker,
}) {
  let controller = null;
  let requestStarted = false;
  let snapshot = isAuthenticated && ticker
    ? { ...EMPTY_DCF_SAVES_SNAPSHOT, status: 'loading' }
    : EMPTY_DCF_SAVES_SNAPSHOT;
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const setSnapshot = (nextSnapshot) => {
    snapshot = nextSnapshot;
    notify();
  };

  const loadDcfSaves = async () => {
    if (!isAuthenticated || !ticker) {
      setSnapshot(EMPTY_DCF_SAVES_SNAPSHOT);
      return;
    }

    requestStarted = true;
    controller = new AbortController();
    setSnapshot({ ...EMPTY_DCF_SAVES_SNAPSHOT, status: 'loading' });

    try {
      const token = await getAccessTokenSilently({ detailedResponse: false });
      const { data } = await axios.get(
        `${apiUrl}/api/dcf/saves?ticker=${encodeURIComponent(ticker)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );

      const saves = Array.isArray(data) ? data : data?.saves || [];
      setSnapshot({
        saves: sortDcfSaves(saves),
        error: '',
        status: 'success',
      });
    } catch (err) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') {
        return;
      }

      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not load saved DCF scenarios.';
      setSnapshot({
        saves: [],
        error: message,
        status: 'error',
      });
    }
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1 && !requestStarted) loadDcfSaves();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          if (controller) {
            controller.abort();
            controller = null;
          }
          requestStarted = false;
        }
      };
    },
  };
}

export function useDcfSaves({
  apiUrl,
  getAccessTokenSilently,
  isAuthenticated,
  ticker,
}) {
  const store = useMemo(
    () => createDcfSavesStore({
      apiUrl,
      getAccessTokenSilently,
      isAuthenticated,
      ticker,
    }),
    [apiUrl, getAccessTokenSilently, isAuthenticated, ticker]
  );

  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  return useMemo(
    () => ({
      saves: snapshot.saves,
      error: snapshot.error,
      loading: snapshot.status === 'loading',
      status: snapshot.status,
    }),
    [snapshot]
  );
}
